import {
  applyMiddleware,
  combineReducers,
  compose,
  createStore,
  Store,
} from "redux";

import rootSaga from "./RootSaga";

import { connectRouter, routerMiddleware } from "connected-react-router";
import { createBrowserHistory, History } from "history";

import { composeWithDevTools } from "redux-devtools-extension";

// Middlewares
import createSagaMiddleware from "redux-saga";
import { persistStore } from 'redux-persist';
import { Persistor } from 'redux-persist/es/types';

// Reducers
import MainReducer from "./main/MainReducer";
import { reducer as oidcReducer } from "redux-oidc";
import CustomerReducer from "./customer/CustomerReducer";
import AlertsReducer from "./alerts/AlertsReducer";
import ContentDialogReducer from './contentDialog/ContentDialogReducer';
import DataGridReducer from "./dataGrid/DataGridReducer";
import SidebarReducer from './sidebar/SidebarReducer';
import MessagesReducer from "./messages/MessagesReducer";
import AdminUserReducer from './adminUser/AdminUserReducer';
import MarketingConsentsReducer  from './marketingConsents/MarketingConsentsReducer';
import ContractsCountReducer from "./contractsCount/ContractsCountReducer";
import RequirementTypesReducer from "./requirementTypes/RequirementTypesReducer";
import { ContractingReducer } from "./contracting/ContractingReducer";

// Sagas

import IAppState from "./IAppState";

// Configuration
import { getDynamicConfig } from "infrastructure/configuration/configurationHelper";
import { configuredPersistReducer } from "./reduxPersist";

const dynamicConfig = getDynamicConfig();

interface ConfiguredStore {
  store: Store<IAppState>;
  persistor: Persistor;
} 

function configureStore(
  history: History,
  initialState: IAppState
): ConfiguredStore {
  // ---REDUCERS---
  const rootReducer = combineReducers<IAppState>({
    router: connectRouter(history),
    oidc: oidcReducer,
    main: MainReducer,
    customer: CustomerReducer,
    alerts: AlertsReducer,
    contentDialog: ContentDialogReducer,
    dataGrid: DataGridReducer,
    sidebar: SidebarReducer,
    messages: MessagesReducer,
    adminUser: AdminUserReducer,
    marketingConsents: MarketingConsentsReducer,
    contractsCount: ContractsCountReducer,
    requirementTypes: RequirementTypesReducer,
    contracting: ContractingReducer
  });

  const persistedReducer = configuredPersistReducer(rootReducer);

  // ---MIDDLEWARES---
  // Saga
  const sagaMiddleware = createSagaMiddleware();

  const middleware = [routerMiddleware(history), sagaMiddleware];

  // ---ENHANCERS---
  const composeEnhancers = composeWithDevTools({});

  // ---CREATE STORE---
  const store = createStore(
    persistedReducer,
    initialState,
    composeEnhancers(compose(applyMiddleware(...middleware)))
  );

  // ---RUN---
  sagaMiddleware.run(rootSaga);

  // create persistor which save store to browser storage
  let persistor = persistStore(store);

  return {
    store: store,
    persistor: persistor
  };
}

// Create browser history to use in the Redux store
export const history = createBrowserHistory({
  basename: dynamicConfig.portalUrlBasename ?? undefined,
});

// Get the application-wide store instance, prepopulating with state from the server where available.
export const { store, persistor } = configureStore(history, (window as any).initialReduxState);